/// <reference path="index.ts" />
import produce from "immer";
import moment, { Moment } from "moment";
import { useMemo } from "react";
import { DeepPartial } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { array, mixed, number, object, reach, string } from "yup";
import { Pageable } from "../../../../services/pageable";
import { countries } from "../../../common/models";
import { getFields, validationFields } from "../../../common/models/fields";
import { UsersDTO } from "../../../users/models/dto";
import { VerificationService } from "../../service";
import {
  DueDiligenceFileCategory,
  DueDiligenceFormStatus,
  DueDiligenceKycType,
  useDueDiligenceKycType,
} from "../internal";
import { VerificationDTO as KYCVerificationDTO } from "./kyc";

export namespace VerificationDTO {
  export namespace Responses {
    export namespace DueDiligence {
      export namespace Config {
        export namespace Get {
          export interface T {
            callbackUrl: string;
            id: string;
            dueDiligenceId: string;
            createdAt: Moment;
            emailAddress: string;
            enabledOptionalFields: Array<
              "WHITEPAPER" | "TOKENOMICS" | "COMPANY_MEMBERS"
            >;
            expiresAt?: Moment;
            formBaseUrl: string;
            header: {
              css: {
                backgroundColor: {
                  degree: number;
                  end: string;
                  endAlpha: number;
                  start: string;
                  startAlpha: number;
                };
              };
            };
            locale: string;
            merchantId: string;
            sendEmails: boolean;
            url: string;
          }

          export function processResponse(body: any): T {
            return produce<any, UsersDTO.Responses.GetSingle.T>(
              body,
              (body: any) => {
                body.expiresAt = body.expiresAt
                  ? moment.utc(body.expiresAt)
                  : body.expiresAt;
                body.createdAt = moment.utc(body.createdAt);
              }
            );
          }
        }

        export namespace GetAll {
          export interface T {
            content: Get.T[];
            pageable: Pageable.Response.T;
          }

          export const defaults: T = {
            content: [],
            pageable: Pageable.Response.defaults,
          };
        }

        export namespace Post {
          export type T = Get.T;
        }

        export namespace Put {
          export type T = Get.T;
        }
      }

      export namespace OptionalFields {
        export namespace Enable {
          export type T = any;
        }
        export namespace Disable {
          export type T = Enable.T;
        }
      }

      export namespace Form {
        export namespace Get {
          export interface T {
            id: string;
            config: {
              expiresAt: Date;
              merchantId: string;
            };
            attachments: Array<{
              fileName: string;
              id: string; //uuid
              type: typeof DueDiligenceFileCategory.T;
              uploadedAt: Moment;
            }>;
            lastUpdatedAt: Date;
            contactDetails: {
              firstName: string;
              middleName: string;
              lastName: string;
              phone: string;
              emailAddress: string;
            };
            participants: Array<{
              id: string;
              firstName: string;
              lastName: string;
              emailAddress: string;
            }>;
            status: typeof DueDiligenceFormStatus.T;
            kycProcessed: number;
            totalKycToBeProcessed: number;
            kycs: [
              {
                types: Array<DueDiligenceKycType["T"]>;
                details: KYCVerificationDTO.Responses.GetSingleKyc.T;
              }
            ];
            kyb?: {
              companyName: string;
              taxCountry: typeof countries.T;
              estimatedAnnualTurnover: number;
              taxIdentificationNo: string;
              whitepaperUrl: string;
              tokenomicsUrl: string;
              details: KYCVerificationDTO.Responses.GetSingleKyb.T;
            };
          }

          export function parse(body: any) {
            return produce<any, any, T>(body, (state: any) => {
              state.attachments.forEach((x: any) => {
                x.uploadedAt = moment(x.uploadedAt);
              });
              return state as T;
            });
          }

          export type WithMerchant = Omit<T, "config"> & {
            config: Omit<T["config"], "merchantId"> & {
              merchant: UsersDTO.Responses.GetSingle.T;
            };
          };
        }

        export namespace GetPublic {
          export type T = Omit<
            Get.T,
            "kycs" | "kyb" | "contactDetails" | "participants"
          > & {
            contactDetails: Pick<Get.T["contactDetails"], "emailAddress">;
            participants: Array<Pick<Get.T["participants"][number], "id">>;
          };

          export type WithMerchant = Omit<T, "config"> & {
            config: Omit<T["config"], "merchantId"> & {
              merchant: UsersDTO.Responses.GetSingle.T;
            };
          };
        }

        export namespace GetAll {
          export interface T {
            content: Get.T[];
            pageable: Pageable.Response.T;
          }

          export interface WithMerchant {
            content: Get.WithMerchant[];
            pageable: Pageable.Response.T;
          }

          export const defaults: T = {
            content: [],
            pageable: Pageable.Response.defaults,
          };
        }

        export namespace Post {
          export namespace Attachments {
            export type T = any;
          }
          export namespace Init {
            export type T = {
              id: string;
            };
          }
          export namespace Final {
            export type T = any;
          }
        }

        export namespace GetParticipantDetails {
          export interface T {
            firstName: string;
            lastName: string;
            emailAddress: string;
          }
        }
      }
    }
  }

  export namespace Requests {
    export namespace DueDiligence {
      export namespace Config {
        export namespace Post {
          export interface T {
            merchantId: string;
            emailAddress: string;
            expiresAt?: Date;
            callbackUrl?: string;
            companyName: string;
            formBaseUrl: string;
            header: {
              image: File | null;
              css: {
                backgroundColor: {
                  degree: number;
                  start: string;
                  startAlpha: number;
                  end: string;
                  endAlpha: number;
                };
              };
            };
            sendEmails: boolean;
          }

          export const defaults: DeepPartial<T> = {
            emailAddress: "",
            formBaseUrl: window.location.origin + "/due-diligence-form",
            sendEmails: true,
            callbackUrl: "",
            header: {
              css: {
                backgroundColor: {
                  degree: 5,
                  end: "#54c1Fc",
                  start: "#000000",
                  endAlpha: 100,
                  startAlpha: 1,
                },
              },
            },
          };

          export const useFields = () => {
            const { t } = useTranslation("fields");

            const merchantId = {
              name: validationFields.merchantId.useName(),
              schema: validationFields.merchantId.useSchema(),
            };

            const emailAddress = {
              name: validationFields.email.useName(),
              schema: validationFields.email.useSchema(),
            };

            const expiresAtSchemaFn = validationFields.expiresAt.useSchema();

            const expiresAt = {
              name: validationFields.expiresAt.useName(),
              schema: useMemo(
                () =>
                  expiresAtSchemaFn.min(
                    new Date(),
                    "Date must be in the future"
                  ),
                [expiresAtSchemaFn]
              ),
            };

            const callbackUrl = {
              name: validationFields.callbackUrl.useName(),
              schema: validationFields.callbackUrl.useSchema(),
            };

            const formBaseUrl = {
              name: validationFields.formBaseUrl.useName(),
              schema: validationFields.formBaseUrl.useSchema(),
            };

            const sendEmails = {
              name: validationFields.sendEmails.useName(),
              schema: validationFields.sendEmails.useSchema(),
            };

            const header = {
              name: useMemo(() => t("header"), [t]),
              schema: useMemo(
                () =>
                  object()
                    .shape({
                      image: mixed()
                        .required(
                          t("required-field", {
                            ns: "errors",
                            field: t("image"),
                          })
                        )
                        .label(t("image")),
                      css: object()
                        .shape({
                          backgroundColor: object()
                            .shape({
                              degree: number()
                                .required(
                                  t("required-field", {
                                    ns: "errors",
                                    field: t("degree"),
                                  })
                                )
                                .label(t("degree")),
                              start: string()
                                .required(
                                  t("required-field", {
                                    ns: "errors",
                                    field: t("start"),
                                  })
                                )
                                .label(t("start")),
                              startAlpha: number()
                                .required(
                                  t("required-field", {
                                    ns: "errors",
                                    field: t("start-alpha"),
                                  })
                                )
                                .label(t("start-alpha")),
                              end: string()
                                .required(
                                  t("required-field", {
                                    ns: "errors",
                                    field: t("end"),
                                  })
                                )
                                .label(t("end")),
                              endAlpha: number()
                                .required(
                                  t("required-field", {
                                    ns: "errors",
                                    field: t("end-alpha"),
                                  })
                                )
                                .label(t("end-alpha")),
                            })
                            .label(t("background-colour")),
                          textColor: string().label(t("text-colour")),
                        })
                        .label(t("css")),
                    })
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("header"),
                      })
                    )
                    .label(t("header")),
                [t]
              ),
            };

            const companyName = {
              name: useMemo(() => t("company-name"), [t]),
              schema: string().required().label(t("company-name")),
            };

            return useMemo(
              () =>
                getFields({
                  merchantId,
                  companyName,
                  emailAddress,
                  expiresAt,
                  callbackUrl,
                  formBaseUrl,
                  sendEmails,
                  header,
                }),
              [
                merchantId.name,
                merchantId.schema,
                companyName.name,
                companyName.schema,
                emailAddress.name,
                emailAddress.schema,
                expiresAt.name,
                expiresAt.schema,
                callbackUrl.name,
                callbackUrl.schema,
                formBaseUrl.name,
                formBaseUrl.schema,
                sendEmails.name,
                sendEmails.schema,
                header.name,
                header.schema,
              ]
            );
          };
        }

        export namespace Put {
          export type T = Post.T;

          export const useFields = Post.useFields;
        }
      }

      export namespace OptionalFields {
        export namespace Enable {
          export interface T {
            configId: string;
            field: "WHITEPAPER" | "TOKENOMICS" | "COMPANY_MEMBERS";
          }
        }
        export namespace Disable {
          export type T = Enable.T;
        }
      }

      export namespace Form {
        export namespace Post {
          export namespace Init {
            export interface T {
              contact: {
                firstName: string;
                middleName: string;
                lastName: string;
                phone: string;
                emailAddress: string;
              };
              kycsToInput: number;
              participants: Array<{
                firstName: string;
                lastName: string;
                emailAddress: string;
              }>;
            }

            export const useFields = (configId: string) => {
              const { t } = useTranslation("fields");

              const firstName = {
                name: validationFields.firstName.useName(),
                schema: validationFields.firstName.useSchema(),
              };

              const lastName = {
                name: validationFields.lastName.useName(),
                schema: validationFields.lastName.useSchema(),
              };

              const middleName = {
                name: validationFields.middleName.useName(),
                schema: validationFields.middleName.useSchema(),
              };

              const phone = {
                name: validationFields.phone.useName(),
                schema: validationFields.phone.useSchema(),
              };

              const emailAddress = {
                name: validationFields.email.useName(),
                schema: validationFields.email.useSchema(),
              };

              const kycsToInput = {
                name: useMemo(() => t("kycs-to-input"), [t]),
                schema: useMemo(() => number().required().min(1), []),
              };

              const participants = {
                name: useMemo(() => t("participants"), [t]),
                schema: useMemo(
                  () =>
                    array().of(
                      object().shape({
                        firstName: firstName.schema,
                        lastName: lastName.schema,
                        emailAddress: emailAddress.schema.test(
                          "invalid-email",
                          t("email-address-invalid", { ns: "errors" }),
                          async (value) => {
                            if (value === undefined || value === "")
                              return false;
                            await VerificationService.validateParticipantEmail(
                              configId,
                              value
                            );
                            return true;
                          }
                        ),
                      })
                    ),
                  [
                    firstName.schema,
                    lastName.schema,
                    emailAddress.schema,
                    configId,
                    t,
                  ]
                ),
              };

              const contact = {
                name: useMemo(() => t("contact"), [t]),
                schema: useMemo(
                  () =>
                    object()
                      .shape({
                        firstName: firstName.schema.label(firstName.name),
                        middleName: middleName.schema.label(middleName.name),
                        lastName: lastName.schema.label(lastName.name),
                        phone: phone.schema.label(phone.name),
                        emailAddress: emailAddress.schema.label(
                          emailAddress.name
                        ),
                      })
                      .required(),
                  [
                    firstName.name,
                    firstName.schema,
                    middleName.name,
                    middleName.schema,
                    lastName.name,
                    lastName.schema,
                    phone.name,
                    phone.schema,
                    emailAddress.name,
                    emailAddress.schema,
                  ]
                ),
              };

              return useMemo(
                () =>
                  getFields({
                    contact,
                    kycsToInput,
                    participants,
                  }),
                [
                  contact.name,
                  contact.schema,
                  kycsToInput.name,
                  kycsToInput.schema,
                  participants.name,
                  participants.schema,
                ]
              );
            };
          }

          export namespace Attachments {
            export interface T {
              prevEmailAddress?: string;
              emailAddress?: string;
              type?: string;
              category?: typeof DueDiligenceFileCategory.T;
              file?: File;
            }

            export const useFields = () => {
              const { t } = useTranslation("fields");

              const emailAddress = validationFields.email.useSchema();
              const prevEmailAddress = useMemo(
                () => emailAddress.notRequired().label(t("previous-email")),
                [emailAddress, t]
              );

              const type = useMemo(
                () =>
                  string()
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("type"),
                      })
                    )
                    .label("type"),
                [t]
              );

              const category = useMemo(
                () =>
                  mixed()
                    .oneOf(DueDiligenceFileCategory.keys)
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("category"),
                      })
                    )
                    .label("category"),
                [t]
              );

              const file = useMemo(
                () =>
                  mixed()
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("file"),
                      })
                    )
                    .test(
                      "is-large",
                      t(`file-size-cannot-exceed-6-mb`),
                      (file: any) =>
                        !(file instanceof File) || file.size <= 6 * 1024 * 1024
                    )
                    .label("file"),
                [t]
              );

              return useMemo(
                () =>
                  object().shape({
                    prevEmailAddress,
                    emailAddress,
                    type,
                    category,
                    file,
                  }),
                [emailAddress, type, category, file]
              );
            };
          }

          export namespace Final {
            type KycRequest = Participant.T;

            export interface T {
              companyDetails: {
                companyRegistrationNumber: string;
                companyJurisdictionCountryCode: typeof countries.T;
                taxCountry: typeof countries.T;
                estimatedAnnualTurnover: number;
                taxIdentificationNo: string;
                whitepaperUrl: string;
                tokenomicsUrl: string;
              };
              kycRequests: Array<KycRequest>;
            }

            export const useValidations = (
              dueDiligenceId?: string,
              enabledTokenomics?: boolean,
              enabledWhitepaper?: boolean
            ) => {
              const { t } = useTranslation("fields");

              const emailAddressSchema = validationFields.email.useSchema();

              const companyDetails = useMemo(
                () =>
                  object().shape({
                    companyRegistrationNumber: string()
                      .required()
                      .label(t("company-registration-number")),
                    companyJurisdictionCountryCode: mixed()
                      .required()
                      .label(t("company-jurisdiction-country-code")),
                    taxCountry: mixed()
                      .oneOf<typeof countries.T>(countries.keys)
                      .required()
                      .label(t("tax-country")),
                    estimatedAnnualTurnover: number()
                      .required()
                      .label(t("estimated-annual-turnover")),
                    taxIdentificationNo: string()
                      .required()
                      .label(t("tax-identification-no")),
                    ...(enabledWhitepaper
                      ? {
                          whitepaperUrl: string()
                            .url()
                            .label(t("whitepaper-url"))
                            .required(),
                        }
                      : {}),

                    ...(enabledTokenomics
                      ? {
                          tokenomicsUrl: string()
                            .url()
                            .label(t("tokenomics-url"))
                            .required(),
                        }
                      : {}),
                  }),
                [t, enabledWhitepaper, enabledTokenomics]
              );

              const {
                validation: participantSchema,
              } = Participant.useSchemas();

              const singleKycRequest = useMemo(
                () =>
                  participantSchema.shape({
                    userDetails: reach(participantSchema, "userDetails").shape({
                      emailAddress: emailAddressSchema.test(
                        "email-already-in-use",
                        "The email you provided has been used for another KYC",
                        async (value) => {
                          if (
                            value === undefined ||
                            dueDiligenceId === undefined ||
                            value === ""
                          )
                            return false;
                          try {
                            await VerificationService.validateContactPersonKyc(
                              dueDiligenceId,
                              value
                            );
                            return true;
                          } catch (err: any) {
                            return false;
                          }
                        }
                      ),
                    }),
                  }),
                [dueDiligenceId, emailAddressSchema, participantSchema]
              );

              const kycRequests = useMemo(() => array().of(singleKycRequest), [
                singleKycRequest,
              ]);

              return {
                validation: useMemo(
                  () =>
                    object().shape({
                      companyDetails,
                      kycRequests,
                    }),
                  [companyDetails, kycRequests]
                ),
                singleKycRequest,
              };
            };
          }

          export namespace Participant {
            interface Address {
              streetAddress: string;
              city: string;
              zip: string;
              state?: string;
              country: typeof countries.T;
            }

            interface UserDetails {
              firstName: string;
              middleName: string;
              lastName: string;
              emailAddress: string;
              dateOfBirth: Date;
              nationality: typeof countries.T;
              address: Address;
              locale: string;
            }

            export interface T {
              userDetails: UserDetails;
              note?: string;
              dueDiligenceKycTypes: Array<DueDiligenceKycType["T"]>;
              mobileCountryCode: string;
              mobileNumber: string;
              socialMediaLinks: Array<string>;
              identityDocumentDetails: {
                number: string;
                issueDate: Date;
                expiryDate: Date;
              };
            }

            export const useSchemas = () => {
              const { t } = useTranslation("fields");
              const DueDiligenceKycTypes = useDueDiligenceKycType();

              const firstNameSchema = validationFields.firstName.useSchema();
              const middleNameSchema = validationFields.middleName.useSchema();
              const lastNameSchema = validationFields.lastName.useSchema();
              const emailAddressSchema = validationFields.email.useSchema();
              const phoneSchema = validationFields.phone.useSchema();
              const phoneCountryCodeSchema = validationFields.phoneCountryCode.useSchema();
              const dateOfBirthSchema = validationFields.dateOfBirth.useSchema();
              const nationalitySchema = validationFields.nationality.useSchema();
              const streetAddressSchema = validationFields.streetAddress.useSchema();
              const citySchema = validationFields.city.useSchema();
              const zipSchema = validationFields.zip.useSchema();
              const stateSchemaFn = validationFields.state.useSchema();
              const stateSchema = useMemo(
                () =>
                  stateSchemaFn.when("country", {
                    is: "US",
                    then: (s) =>
                      s.required(
                        t("required-field", {
                          ns: "errors",
                          field: t("state"),
                        })
                      ),
                    otherwise: (s) => s.notRequired(),
                  }),
                [stateSchemaFn]
              );

              const countrySchema = validationFields.country.useSchema();
              const identityDocumentNumberSchema = validationFields.identityDocumentNumber.useSchema();
              const identityDocumentExpiryDateSchema = validationFields.identityDocumentExpiryDate.useSchema();
              const identityDocumentIssueDateSchema = validationFields.identityDocumentIssueDate.useSchema();
              const userDetails = useMemo(
                () =>
                  object()
                    .shape({
                      firstName: firstNameSchema,
                      middleName: middleNameSchema,
                      lastName: lastNameSchema,
                      emailAddress: emailAddressSchema,
                      dateOfBirth: dateOfBirthSchema,
                      nationality: nationalitySchema,
                      address: object()
                        .shape({
                          streetAddress: streetAddressSchema,
                          city: citySchema,
                          zip: zipSchema,
                          state: stateSchema,
                          country: countrySchema,
                        })
                        .required(),
                      locale: string().required().label(t("locale")),
                    })
                    .label(t("user-details")),
                [
                  t,
                  firstNameSchema,
                  middleNameSchema,
                  lastNameSchema,
                  emailAddressSchema,
                  dateOfBirthSchema,
                  nationalitySchema,
                  streetAddressSchema,
                  citySchema,
                  zipSchema,
                  stateSchema,
                  countrySchema,
                ]
              );

              return {
                validation: useMemo(
                  () =>
                    object().shape({
                      userDetails: userDetails.required(),
                      note: string().label(t("note")),
                      dueDiligenceKycTypes: array()
                        .of(
                          mixed<typeof DueDiligenceKycTypes.T>().oneOf(
                            DueDiligenceKycTypes.keys
                          )
                        )
                        .compact()
                        .ensure()
                        .label(t("due-diligence-kyc-types"))
                        .min(1)
                        .required(),
                      mobileCountryCode: phoneCountryCodeSchema,
                      mobileNumber: phoneSchema,
                      socialMediaLinks: array()
                        .of(string())
                        .label(t("social-media-links")),
                      identityDocumentDetails: object().shape({
                        number: identityDocumentNumberSchema,
                        issueDate: identityDocumentIssueDateSchema,
                        expiryDate: identityDocumentExpiryDateSchema,
                      }),
                    }),
                  [
                    t,
                    userDetails,
                    phoneCountryCodeSchema,
                    phoneSchema,
                    identityDocumentNumberSchema,
                    identityDocumentIssueDateSchema,
                    identityDocumentExpiryDateSchema,
                    DueDiligenceKycTypes,
                  ]
                ),
                userDetails,
              };
            };
          }

          export namespace ParticipantAttachments {
            export interface T {
              type?: string;
              category?: typeof DueDiligenceFileCategory.T;
              file?: File;
            }

            export const useFields = () => {
              const { t } = useTranslation("fields");

              const type = useMemo(
                () =>
                  string()
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("type"),
                      })
                    )
                    .label("type"),
                [t]
              );

              const category = useMemo(
                () =>
                  mixed()
                    .oneOf(DueDiligenceFileCategory.keys)
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("category"),
                      })
                    )
                    .label("category"),
                [t]
              );

              const file = useMemo(
                () =>
                  mixed()
                    .required(
                      t("required-field", {
                        ns: "errors",
                        field: t("file"),
                      })
                    )
                    .test(
                      "is-large",
                      t(`file-size-cannot-exceed-6-mb`),
                      (file: any) =>
                        !(file instanceof File) || file.size <= 6 * 1024 * 1024
                    )
                    .label("file"),
                [t]
              );

              return useMemo(
                () =>
                  object().shape({
                    type,
                    category,
                    file,
                  }),
                [type, category, file]
              );
            };
          }
        }
      }
    }
  }
}
